import React, { useState, useEffect } from "react";
import "./Budget.css";
import { ArrowLeft, X, ChevronDown } from "lucide-react";
import CalendarIcon from "../../Assets/svg/calendar.svg";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { postApi, getApi, putApi } from "../common/api-client";
import CommonAsyncSelect from "../common/common-async-select";
import SearchWithAPI from "../common/searchbox/searchbox.js";
import { Row, Col } from "react-bootstrap";

const BudgetCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pricingType, setPricingType] = useState("Time based");

  const [timingType, setTimingType] = useState("Hours");
  const [periodType, setPeriodType] = useState("Daily");
  const [duration, setDuration] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [totalCost, setTotalCost] = useState("");

  // New Form states
  const [budgetTitle, setBudgetTitle] = useState("");
  const [project, setProject] = useState(null);
  const [types, setTypes] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalDuration, setTotalDuration] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("draft");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [validations, setValidations] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL || "";
  const { id } = useParams();

  const handleCancel = () => {
    if (id) {
      if (location.state?.from === "list") {
        navigate("/budget");
      } else {
        navigate("/budget/view", { state: { id: Number(id) } });
      }
    } else {
      navigate("/budget");
    }
  };


  useEffect(() => {
    if (id) {
      fetchBudgetData();
    }
  }, [id]);

  const fetchBudgetData = async () => {
    try {
      const response = await getApi(`budget/edit/${id}`);
      if (response?.success) {
        const data = response.budget || response.data?.budget || response;
        setBudgetTitle(data.title || data.budget_title || "");

        // Handle Project - robust mapping
        const pId = data.project_id || data.project?.id || data.project?.value;
        const pLabel = data.project_name || (typeof data.project === "string" ? data.project : data.project?.name || data.project?.label);

        if (pId || pLabel) {
          setProject({
            id: pId || null,
            label: pLabel || `Project #${pId}`,
          });
        }

        // Map types (components) - handle array of strings or objects
        if (data.types && Array.isArray(data.types)) {
          setTypes(
            data.types.map((t) => {
              if (typeof t === "object") return { id: t.id, label: t.name || t.label || "" };
              return { id: t, label: t };
            }),
          );
        }

        const pricingTypeMapped = (data.pricing_type || "").toLowerCase().includes("fixed") ? "Fixed" : "Time based";
        const timingTypeMapped = (data.timing_type || "").toLowerCase().includes("day") ? "Days" : "Hours";

        setPricingType(pricingTypeMapped);
        setTimingType(timingTypeMapped);

        if (data.period_type) {
          const pt = data.period_type.charAt(0).toUpperCase() + data.period_type.slice(1).toLowerCase();
          setPeriodType(pt);
        }

        setDuration(data.duration || "");
        setUnitCost(data.cost_per_hour || data.unit_cost || "");
        setTotalCost(data.total_cost || "");
        setStartDate(data.start_date || "");
        setEndDate(data.end_date || "");
        setDescription(data.description || "");
        setStatus(data.status || "draft");

        // Map users - handle array of strings or objects
        if (data.users && Array.isArray(data.users)) {
          setSelectedUsers(
            data.users.map((u) => {
              if (typeof u === "object") return { id: u.id, label: u.name || u.label || "" };
              return { id: u, label: u };
            }),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching budget data:", error);
    }
  };

  const getPeriodCount = (start, end, period) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    if (e < s) return 0;

    if (period === "Daily") {
      const diffTime = Math.abs(e - s);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } else if (period === "Monthly") {
      return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
    } else if (period === "Yearly") {
      return e.getFullYear() - s.getFullYear() + 1;
    }
    return 1;
  };

  useEffect(() => {
    if (pricingType === "Time based") {
      const periodCount = getPeriodCount(startDate, endDate, periodType);
      let calculatedTotalDuration = 0;
      let calculatedTotalCost = 0;
      const cost = parseFloat(unitCost) || 0;

      if (timingType === "Days") {
        if (periodType === "Daily") {
          // Automatically calculate total days from start/end date range
          const totalDays = getPeriodCount(startDate, endDate, "Daily");
          calculatedTotalDuration = totalDays;
        } else {
          // Manual days per period * period count (Monthly/Yearly)
          const daysPerPeriod = parseInt(duration) || 0;
          calculatedTotalDuration = daysPerPeriod * periodCount;
        }
        calculatedTotalCost = calculatedTotalDuration * cost;
        setTotalDuration(calculatedTotalDuration ? `${calculatedTotalDuration} days` : "");
      } else if (timingType === "Hours" && duration && duration.includes(":")) {
        const [hoursStr, minutesStr] = (duration || "").split(":");
        const hours = parseInt(hoursStr) || 0;
        const minutes = parseInt(minutesStr) || 0;
        const hoursPerPeriod = hours + minutes / 60;

        calculatedTotalDuration = hoursPerPeriod * periodCount;
        calculatedTotalCost = calculatedTotalDuration * cost;

        // Convert back to HH:mm for display
        const wholeHours = Math.floor(calculatedTotalDuration);
        const remainingMinutes = Math.round((calculatedTotalDuration - wholeHours) * 60);
        setTotalDuration(calculatedTotalDuration ? `${wholeHours}:${remainingMinutes.toString().padStart(2, "0")} hrs` : "");
      }
      setTotalCost(calculatedTotalCost ? calculatedTotalCost.toFixed(2) : "");
    }
  }, [pricingType, timingType, duration, unitCost, startDate, endDate, periodType]);

  const handleTimingTypeChange = (type) => {
    setTimingType(type);
    setDuration("");
    setUnitCost("");
    setTotalDuration("");
    setTotalCost("");
  };

  const handlePricingTypeChange = (type) => {
    setPricingType(type);
    setDuration("");
    setUnitCost("");
    setTotalDuration("");
    setTotalCost("");
  };

  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleProjectChange = (val) => {
    setProject(val);
    setTypes([]);
  };

  const handleTypeSelect = (selectedOption) => {
    if (!selectedOption) return;
    const selectedId = selectedOption.id || selectedOption.value;
    if (selectedId && !types.find((t) => (t.id || t.value) === selectedId)) {
      setTypes([...types, selectedOption]);
    }
  };

  const removeType = (index) => {
    setTypes(types.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setValidations({});

    // Map pricingType and timingType to backend format
    const mappedPricingType = pricingType === "Time based" ? "time_based" : "fixed";
    const mappedTimingType = timingType === "Hours" ? "hours" : "days";
    const mappedPeriodType = pricingType === "Time based" ? periodType.toLowerCase() : null;

    const requestBody = {
      budget_title: budgetTitle,
      project_id: project?.id || project?.value || null,
      type: types.map((t) => (typeof t === "object" ? t.id || t.value : t)).filter((val) => val !== null && val !== undefined && val !== ""),
      pricing_type: mappedPricingType,
      timing_type: mappedTimingType,
      period_type: mappedPeriodType,
      duration: duration || "0",
      start_date: startDate || null,
      end_date: endDate || null,
      cost_per_hour: pricingType === "Time based" ? parseFloat(unitCost || 0) : null,
      total_cost: parseFloat(totalCost || 0) || 0,
      description: description || "",
      status: status || "draft",
      users: selectedUsers.map((u) => (typeof u === "object" ? u.id || u.value : u)).filter((val) => val !== null && val !== undefined && val !== ""),
    };

    try {
      const response = id ? await putApi(`budget/${id}`, requestBody) : await postApi("budget", requestBody);

      if (response?.success) {
        navigate("/budget");
      }
    } catch (err) {
      const errorData = err?.response?.data || err?.response;
      if (errorData?.error) {
        const validationTemp = {};
        Object.entries(errorData.error).forEach(([key, value]) => {
          validationTemp[key] = Array.isArray(value) && value.length > 0 ? value[0] : "";
        });
        setValidations(validationTemp);
      } else {
        console.error("Budget save error:", err);
      }
    }
  };

  return (
    <div className="budget-container">
      <div className="budget-create-header">
        <div className="header-left-flex">
          <button className="back-btn" onClick={handleCancel}>
            <ArrowLeft size={20} />
          </button>
          <h1>{id ? "Edit Budget" : "New Budget"}</h1>
        </div>
        <div className="header-right-btns">
          <button className="cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="save-btn" onClick={handleSave}>
            Save
          </button>
        </div>

      </div>

      <div className="budget-form-card">
        <div className="form-section-header">
          <h2>Budget details</h2>
          <div className="status-badge-container">
            <span>Status :</span>
            <div style={{ position: "relative" }} onMouseLeave={() => setStatusDropdownOpen(false)}>
              <div
                className="status-dropdown"
                onClick={() => {
                  if (status !== "closed") {
                    setStatusDropdownOpen(!statusDropdownOpen);
                  }
                }}
                style={{
                  backgroundColor: status === "active" ? "#e6ffec" : status === "closed" ? "#f0f4f8" : "#fff4e6",
                  color: status === "active" ? "#00a361" : status === "closed" ? "#8790a1" : "#21272c",
                  cursor: status === "closed" ? "default" : "pointer",
                }}
              >
                <span
                  className="status-dot"
                  style={{
                    backgroundColor: status === "active" ? "#00a361" : status === "closed" ? "#8790a1" : "#ff9933",
                  }}
                ></span>
                {status.charAt(0).toUpperCase() + status.slice(1)}
                {status !== "closed" && <ChevronDown size={14} />}
              </div>

              {statusDropdownOpen && (
                <div
                  className="status-dropdown-menu"
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "4px",
                    backgroundColor: "white",
                    border: "1px solid #E5E5E5",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    zIndex: 10,
                    minWidth: "120px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    onClick={() => {
                      if (status === "draft") {
                        setStatus("draft");
                        setStatusDropdownOpen(false);
                      }
                    }}
                    style={{
                      padding: "8px 16px",
                      cursor: status === "draft" ? "pointer" : "not-allowed",
                      opacity: status === "draft" ? 1 : 0.5,
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      borderBottom: "1px solid #F1F1F1",
                      backgroundColor: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (status === "draft") {
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                      }
                    }}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <span className="status-dot" style={{ backgroundColor: "#ff9933" }}></span>
                    Draft
                  </div>
                  <div
                    onClick={() => {
                      setStatus("active");
                      setStatusDropdownOpen(false);
                    }}
                    style={{ padding: "8px 16px", cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", gap: "12px", borderBottom: "1px solid #F1F1F1" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <span className="status-dot" style={{ backgroundColor: "#00a361" }}></span>
                    Active
                  </div>
                  <div
                    onClick={() => {
                      setStatus("closed");
                      setStatusDropdownOpen(false);
                    }}
                    style={{ padding: "8px 16px", cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", gap: "12px" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <span className="status-dot" style={{ backgroundColor: "#8790a1" }}></span>
                    Closed
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="budget-form-body">
          {/* Row 1 */}
          <Row className="budget-row-wrapper">
            <Col lg={6}>
              <div className="form-group">
                <label>Budget Title <span className="text-danger">*</span></label>
                <input type="text" placeholder="Enter here..." className="budget-input" value={budgetTitle} onChange={(e) => setBudgetTitle(e.target.value)} />
                {validations.budget_title && (
                  <div className="text-danger mt-1" style={{ fontSize: "12px", color: "#FF4444" }}>
                    {validations.budget_title}
                  </div>
                )}
              </div>
            </Col>
            <Col lg={3}>
              <div className="form-group">
                <label>Project <span className="text-danger">*</span></label>
                <div className="select-wrapper">
                  <SearchWithAPI onDropdownChange={handleProjectChange} apiUrl={`${apiUrl}get_project`} placeholder="Select Project" selectedValue={project?.label || ""} selectedOption={project} />
                  {validations.project_id && (
                    <div className="text-danger mt-1" style={{ fontSize: "12px", color: "#FF4444" }}>
                      {validations.project_id}
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={3}>
              <div className="form-group">
                <label>Type <span className="text-danger">*</span></label>
                <div className="select-wrapper">
                  <SearchWithAPI
                    key={project?.id || project?.value || "no-project"}
                    onDropdownChange={handleTypeSelect}
                    apiUrl={project ? `${apiUrl}get_parent_component` : `${apiUrl}get_parent_component`}
                    params={project ? { project_id: project.id || project.value } : {}}
                    placeholder={types.length > 0 ? "" : "Select Types"}
                    selectedOption={null}
                    selectedValue=""
                    closeOnSelect={false}
                    headerPrefix={
                      types.length > 0 && (
                        <div className="d-flex flex-wrap mr-2 custom-scroll" style={{ gap: "12px", maxHeight: "80px", overflowY: "auto", padding: "4px" }}>
                          {types.map((t, index) => (
                            <span key={index} className="type-tag" onClick={(e) => e.stopPropagation()}>
                              {t.label}
                              <X
                                size={14}
                                className="remove-tag-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeType(index);
                                }}
                              />
                            </span>
                          ))}
                        </div>
                      )
                    }
                  />
                </div>
                {validations.type && (
                  <div className="text-danger mt-1" style={{ fontSize: "12px", color: "#FF4444" }}>
                    {validations.type}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row className="budget-row-wrapper">
            <Col lg={6}>
              <div className="form-group">
                <label>Description</label>
                <input type="text" placeholder="Enter here..." className="budget-input" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            </Col>
            <Col lg={3}>
              <div className="form-group">
                <label>Start & End date <span className="text-danger">*</span></label>
                <div className="unified-date-range">
                  <div className="date-field">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      onClick={(e) => (typeof e.target.showPicker === "function" ? e.target.showPicker() : null)}
                    />
                    <img src={CalendarIcon} alt="calendar" className="calendar-icon-svg" />
                  </div>
                  <div className="date-divider"></div>
                  <div className="date-field">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      onClick={(e) => (typeof e.target.showPicker === "function" ? e.target.showPicker() : null)}
                    />
                    <img src={CalendarIcon} alt="calendar" className="calendar-icon-svg" />
                  </div>
                </div>
                {(validations.start_date || validations.end_date) && (
                  <div className="text-danger mt-1" style={{ fontSize: "12px", color: "#FF4444" }}>
                    {validations.start_date || validations.end_date}
                  </div>
                )}
              </div>
            </Col>
            <Col lg={3}>
              <div className="form-group">
                <label>Pricing Type</label>
                <div className="radio-group flex-wrap">
                  <label className={`radio-option ${pricingType === "Time based" ? "active" : ""}`}>
                    <input type="radio" name="pricing" checked={pricingType === "Time based"} onChange={() => handlePricingTypeChange("Time based")} />
                    <span className="radio-dot"></span>
                    Time based
                  </label>
                  <label className={`radio-option ${pricingType === "Fixed" ? "active" : ""}`}>
                    <input type="radio" name="pricing" checked={pricingType === "Fixed"} onChange={() => handlePricingTypeChange("Fixed")} />
                    <span className="radio-dot"></span>
                    Fixed
                  </label>
                </div>
              </div>
            </Col>
          </Row>

          {/* Row 3 */}
          {/* Pricing Section */}
          {pricingType === "Fixed" && (
            <Row className="budget-row-wrapper">
              <Col lg={3}>
                <div className="form-group">
                  <label>Total cost</label>
                  <input type="text" placeholder="Enter total cost..." className="budget-input" value={totalCost || ""} onChange={(e) => setTotalCost(e.target.value.replace(/[^0-9]/g, ""))} />
                </div>
              </Col>
            </Row>
          )}

          {pricingType === "Time based" && (
            <div className="budget-pricing-grid">
              {/* Labels Row */}
              <Row className="mb-2">
                <Col lg={2}>
                  <label className="mb-0 label">Timing Type</label>
                </Col>
                <Col lg={3}>
                  <label className="mb-0 label">Period Type</label>
                </Col>
                <Col>
                  <label className="mb-0 label">
                    {timingType === "Days" ? "No. of Days" : `Duration per ${periodType === "Daily" ? "day" : periodType === "Monthly" ? "month" : "year"}`}
                  </label>
                </Col>
                <Col>
                  <label className="mb-0 label">
                    {timingType === "Days" ? "Total Days" : "Total Duration"}
                  </label>
                </Col>
                <Col>
                  <label className="mb-0 label">{timingType === "Days" ? "Cost per day" : "Cost per hour"}</label>
                </Col>
                <Col>
                  <label className="mb-0 label">Total Cost</label>
                </Col>
              </Row>

              {/* Inputs Row */}
              <Row className="align-items-center">
                <Col lg={2}>
                  <div className="radio-group flex-wrap">
                    <label className={`radio-option ${timingType === "Hours" ? "active" : ""}`}>
                      <input type="radio" name="timing" checked={timingType === "Hours"} onChange={() => handleTimingTypeChange("Hours")} />
                      <span className="radio-dot"></span>
                      Hours
                    </label>
                    <label className={`radio-option ${timingType === "Days" ? "active" : ""}`}>
                      <input type="radio" name="timing" checked={timingType === "Days"} onChange={() => handleTimingTypeChange("Days")} />
                      <span className="radio-dot"></span>
                      Days
                    </label>
                  </div>
                </Col>

                <Col lg={3}>
                  <div className="radio-group flex-wrap">
                    {timingType === "Hours" && (
                      <label className={`radio-option ${periodType === "Daily" ? "active" : ""}`}>
                        <input type="radio" name="period" checked={periodType === "Daily"} onChange={() => setPeriodType("Daily")} />
                        <span className="radio-dot"></span>
                        Daily
                      </label>
                    )}
                    <label className={`radio-option ${periodType === "Monthly" ? "active" : ""}`}>
                      <input type="radio" name="period" checked={periodType === "Monthly"} onChange={() => setPeriodType("Monthly")} />
                      <span className="radio-dot"></span>
                      Monthly
                    </label>
                    <label className={`radio-option ${periodType === "Yearly" ? "active" : ""}`}>
                      <input type="radio" name="period" checked={periodType === "Yearly"} onChange={() => setPeriodType("Yearly")} />
                      <span className="radio-dot"></span>
                      Yearly
                    </label>
                  </div>
                </Col>

                <Col>
                  {timingType === "Days" ? (
                    <input type="number" min="1" placeholder="Days" className="budget-input" value={duration} onChange={(e) => setDuration(e.target.value)} style={{ width: "100%" }} />
                  ) : (
                    <input
                      type="text"
                      placeholder="00:00"
                      className="budget-input"
                      style={{ width: "100%" }}
                      value={duration}
                      maxLength={5}
                      onChange={(e) => {
                        let val = e.target.value.replace(/[^0-9:]/g, "");
                        if (val.length === 2 && !val.includes(":") && duration.length < 2) val = val + ":";
                        setDuration(val);
                      }}
                    />
                  )}
                </Col>

                <Col>
                  <input
                    type="text"
                    readOnly
                    value={totalDuration || "0:00"}
                    style={{
                      width: "100%",
                      height: "40px",
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                      borderRadius: "12px",
                      padding: "0 12px",
                      fontSize: "14px",
                      color: "#374151",
                      fontWeight: "500",
                    }}
                  />
                </Col>

                <Col>
                  <div className="cost-input-group" style={{ width: "100%" }}>
                    <div className="currency-select" style={{ minWidth: "40px", padding: "0 8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      ₹
                    </div>
                    <input type="number" min="0" placeholder="Amount..." value={unitCost} onChange={(e) => setUnitCost(e.target.value)} style={{ paddingLeft: "8px" }} />
                  </div>
                </Col>

                <Col>
                  <div
                    style={{
                      width: "100%",
                      height: "40px",
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                      borderRadius: "12px",
                      padding: "0 12px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      color: "#374151",
                      fontWeight: "500",
                    }}
                  >
                    ₹ {totalCost || "0.0"}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </div>

      <div className="budget-form-card users-section">
        <div className="form-section-header">
          <h2>Users <span className="text-danger">*</span></h2>
        </div>
        <div className="form-group full-width">
          <div className="select-wrapper">
            <SearchWithAPI
              onDropdownChange={(val) => {
                if (!val) return;
                const selectedId = val.id || val.value;
                if (selectedId && !selectedUsers.find((u) => (u.id || u.value) === selectedId)) {
                  setSelectedUsers([...selectedUsers, val]);
                }
              }}
              apiUrl={`${apiUrl}get_employee`}
              params={{}}
              placeholder="Search and add users..."
              selectedOption={null}
              selectedValue=""
              selectedOptions={selectedUsers}
              closeOnSelect={false}
            />
            {validations.users && (
              <div className="text-danger mt-1" style={{ fontSize: "12px", color: "#FF4444" }}>
                {validations.users}
              </div>
            )}
          </div>
        </div>

        <div className="added-users-info">
          <span className="added-count">Added users : {selectedUsers.length}</span>
          <div className="users-tags">
            {selectedUsers.map((user) => (
              <div key={user.id || user.value} className="user-tag">
                <div className="user-avatar">
                  <span className="user-initial">{(user.label || "U").charAt(0).toUpperCase()}</span>
                </div>
                <span className="user-name">{user.label}</span>
                <button className="remove-user-btn" onClick={() => setSelectedUsers(selectedUsers.filter((u) => (u.id || u.value) !== (user.id || user.value)))}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetCreate;
